import React from 'react';
import axios from "axios";
import EditorExperience from "../InteractiveExperience";
import { getObjectByUserDataProperty } from "../threeUtils/TransformConversions";

// Create a global reference to store the action handler
window.chatbotActionHandler = null;

const ActionProvider = ({ createChatBotMessage, setState, children }) => {
    const editor = React.useMemo(() => new EditorExperience(), []);

    const handleAmenitySearch = async (query, sessionId = null) => {
        try {
            const reqObj = sessionId
                ? { query, session_id: sessionId }
                : { query };

            const response = await axios.post(
                `https://aitools.aiagentexperts.ai/webhook/amenity-search`,
                reqObj
            );
            return response.data;
        } catch (e) {
            console.error("Error during amenity search:", e);
            throw e;
        }
    }

    const selectLocationPinByName = React.useCallback((amenityName) => {
        if (!amenityName) {
            console.warn('No amenity name provided');
            return;
        }

        const normalizedSearchName = amenityName.trim().toLowerCase();

        // Find the location pin by name (case-insensitive)
        const locationPin = editor.jsonLocationPins.find(pin => {
            const pinName = pin.name?.trim().toLowerCase();
            const amenityTypeName = pin.amenityPinTypes && pin.amenityPinTypes.length > 0
                ? pin.amenityPinTypes[0].name?.trim().toLowerCase()
                : null;

            return pinName === normalizedSearchName || amenityTypeName === normalizedSearchName;
        });

        if (locationPin) {
            console.log('Found location pin by name:', locationPin);
            console.log('Location pin ID:', locationPin.id);

            // Find the pin mesh using the helper function
            const pinMesh = getObjectByUserDataProperty(
                editor.interactiveParent,
                "id",
                locationPin.id
            );

            if (pinMesh) {
                console.log('Found pin mesh, relocating camera');
                editor.camera.relocateCamera(pinMesh);
            } else {
                console.warn('Pin mesh not found for ID:', locationPin.id);
            }
        } else {
            console.warn('No location pin found with name:', amenityName);
            console.log('Available pins:', editor.jsonLocationPins.map(p => p.name));
        }
    }, [editor]);

    const handleUserMessage = React.useCallback(async (message) => {
        console.log('handleUserMessage called with:', message);

        // Add loading message immediately
        const loadingMsg = createChatBotMessage("Searching...", {
            loading: true,
            terminateLoading: true,
            withAvatar: true
        });

        setState((prev) => {
            const currentSessionId = prev.sessionId || null;
            console.log('Current session ID:', currentSessionId);

            // Add loading message to state
            setState((state) => ({
                ...state,
                messages: [...state.messages, loadingMsg],
            }));

            // Call the API
            handleAmenitySearch(message, currentSessionId)
                .then((data) => {
                    console.log('API Response:', data);
                    const { results, message: botMessage, session_id, amenityPinTypes } = data;

                    // Create bot response message with text
                    const botMsg = createChatBotMessage(botMessage);

                    // If there are results, create a message with the options widget
                    if (results && results.length > 0) {
                        const optionsMsg = createChatBotMessage(botMessage, {
                            widget: "amenityOptions",
                            payload: results
                        });

                        // Update state with new session_id and messages, remove loading message
                        setState((state) => {
                            const filteredMessages = state.messages.slice(0, -1); // Remove last message (loading)
                            return {
                                ...state,
                                messages: [...filteredMessages, optionsMsg],
                                sessionId: session_id,
                                results: results,
                                amenityPinTypes: null,
                            };
                        });
                    } else if (amenityPinTypes && amenityPinTypes.length > 0) {
                        // Final response with amenity details
                        const pinName = amenityPinTypes[0].name;
                        const customMessage = pinName
                            ? `Showing you ${pinName} in floorplan`
                            : botMessage;
                        const customBotMsg = createChatBotMessage(customMessage);

                        setState((state) => {
                            const filteredMessages = state.messages.slice(0, -1); // Remove last message (loading)
                            return {
                                ...state,
                                messages: [...filteredMessages, customBotMsg],
                                sessionId: session_id,
                                results: null,
                                amenityPinTypes: amenityPinTypes,
                            };
                        });

                        // Select the location pin by name
                        if (pinName) {
                            selectLocationPinByName(pinName);
                        }
                    } else {
                        // Just a regular message
                        setState((state) => {
                            const filteredMessages = state.messages.slice(0, -1); // Remove last message (loading)
                            return {
                                ...state,
                                messages: [...filteredMessages, botMsg],
                                sessionId: session_id,
                            };
                        });
                    }
                })
                .catch((error) => {
                    console.error('API Error:', error);
                    const errorMsg = createChatBotMessage("Sorry, I encountered an error. Please try again.");
                    setState((state) => {
                        const filteredMessages = state.messages.slice(0, -1); // Remove last message (loading)
                        return {
                            ...state,
                            messages: [...filteredMessages, errorMsg],
                        };
                    });
                });

            return prev;
        });
    }, [setState, createChatBotMessage, selectLocationPinByName]);

    // Store the handler globally so widgets can access it
    React.useEffect(() => {
        window.chatbotActionHandler = handleUserMessage;
        return () => {
            window.chatbotActionHandler = null;
        };
    }, [handleUserMessage]);

    return (
        <div>
            {React.Children.map(children, (child) => {
                return React.cloneElement(child, {
                    actions: {
                        handleUserMessage: handleUserMessage,
                        handleAmenitySearch: handleAmenitySearch,
                    },
                });
            })}
        </div>
    );
};

export default ActionProvider;